
import { Vue, Prop, Watch, Component, Ref, Emit } from "vue-property-decorator";
import { WidgetSettingResource } from "@/models/api/system/widgets";
import { rawApi } from "@/lib/api";
import { Doughnut, Bar } from 'vue-chartjs'
import { getModule } from "vuex-module-decorators";
import BillToSelect from "@/components/form/BillToSelect.vue";
import Select from "@/components/form/Select.vue";

import { SystemModule } from "@/store/modules";
import WidgetModule from "@/store/modules/widgets";
import notifications from "@/lib/notifications";

import {
  Chart,
  registerables,
} from 'chart.js'

const widgetModule = getModule(WidgetModule);
const systemModule = getModule(SystemModule);

Chart.register( ...registerables );

interface MetricParameters
{
    startDays: number;
    endDays: number;
    billTo: string;
    facility: string;
    deliveryForIds: string
}

interface DamageShortMetric
{
    facility: string;
    billTo: string;
    damagedQuantity: number;
    shortQuantity: number;
    quantity: number;
}

interface DamageShortMetricResults
{
    damagedQuantity: number;
    shortQuantity: number;
    totalQuantity: number;
    from: string;
    to: string;
    metrics: DamageShortMetric[]
}

interface DayRange
{
    label: string;
    start: number;
    end: number;
}

@Component({
  components: {
    Doughnut, Bar, Chart, Select
  }
})
export default class DamageShortMetricWidget extends Vue {
    damagedData: any = null;
    shortData: any = null;
    doughnutOptions: any = {
        responsive: true,
        maintainAspectRatio: false,
        plugins:{
            legend: {
                position: 'none'
            }
        }
    };
    barOptions: any = {
        responsive: true,
        maintainAspectRatio: false,
        plugins:{
            legend: {
                position: 'none'
            }
        },
        scales: {
            xAxes: {
                display: false
            }
        }
    };

    loading: boolean = false;
    isEditModalVisible: boolean = false;
    isDrillDownModalVisible: boolean = false;

    doughnutWidth: number = 400;
    doughnutHeight: number = 400;

    accurateColor: string = '#00bfa0';
    shortColor: string = '#0bb4ff';
    damagedColor: string = '#e60049';

    accurate: number = 0;
    short: number = 0;
    damaged: number = 0;
    total: number = 0;
    from: string = '';
    to: string = '';
    metrics: DamageShortMetric[] = [];

    showDamagedDoughnut: boolean = true;
    showShortDoughnut: boolean = true;
    showAmounts: boolean = true;

    dayRange: string = null;

    dayRanges: DayRange[] = [
        { label: 'Today', start: 0, end: 0 },
        { label: 'Last 7 Days', start: 7, end: 0 },
        { label: 'Last 30 Days', start: 30, end: 0 },
        { label: 'Last 60 Days', start: 60, end: 0 },
        { label: 'Last 90 Days', start: 90, end: 0 }
    ];    

    getMetric(value: number) {
        if (this.showAmounts) {
            return `${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
        }
            
        return `${Math.round(value/this.total*100000)/1000}%`;
    }

    metricTitle: string = '';
    metricFacilities: string[] = [];
    metricDeliveryForIds: number[] = [];
    metricBillTo: string = '';
    metricParameters: MetricParameters = {
        startDays: 0,
        endDays: 0,
        billTo: '',
        facility: '',
        deliveryForIds: ''
    };

    @Prop({ required: true})
    widgetSetting: WidgetSettingResource;

    remove() {
        this.$emit('remove', this.widgetSetting.id);
    }

    async mounted() {
        this.loadMetricData();
    }

    async loadMetricData() {
        this.loading = true;
        var p: MetricParameters = JSON.parse(this.widgetSetting.settings);
        const response = await rawApi.get<DamageShortMetricResults>(`/widget/damageShort?startDays=${p.startDays}&endDays=${p.endDays}&billTo=${p.billTo}&facility=${p.facility}&deliveryForIds=${p.deliveryForIds}`);

        this.damaged = response.data.damagedQuantity;
        this.short = response.data.shortQuantity;
        this.total = response.data.totalQuantity;
        this.accurate = this.total - this.damaged - this.short;
        this.from = response.data.from;
        this.to = response.data.to;
        this.metrics = response.data.metrics;

        this.damagedData = {
            labels: ['Damaged', 'Undamaged'],
            datasets: [
                {
                backgroundColor: [this.damagedColor, this.accurateColor],
                data: [this.damaged, this.total - this.damaged]
                }
            ]
        }

        this.shortData = {
            labels: ['Short', 'Complete'],
            datasets: [
                {
                backgroundColor: [this.shortColor, this.accurateColor],
                data: [this.short, this.total - this.short]
                }
            ]
        }
        this.loading = false;
    }

    configure() {
        this.metricParameters = JSON.parse(this.widgetSetting.settings);
        this.dayRanges.forEach(x => {
            if (x.start == this.metricParameters.startDays && x.end == this.metricParameters.endDays) this.dayRange = x.label;
        });

        this.metricTitle = this.widgetSetting.title;

        var facilityCodes = this.metricParameters.facility.split(',');
        facilityCodes = facilityCodes.filter(x => x != '');

        var deliveryForIds = this.metricParameters.deliveryForIds.split(',').filter(x => x != '');
        this.metricDeliveryForIds = deliveryForIds.map(x => parseInt(x));
        this.metricFacilities = facilityCodes;
        this.isEditModalVisible = true;
    }

    async saveConfiguration() {
        if (!this.dayRange) {
            notifications.error('Error', 'Please select a date range.');
            return;
        }

        var range = this.dayRanges.find(x => x.label == this.dayRange);

        this.isEditModalVisible = false;
        this.metricParameters.facility = this.metricFacilities.join(',');

        this.metricParameters.deliveryForIds = this.metricDeliveryForIds.join(',');
        this.metricParameters.billTo = this.metricBillTo;
        this.widgetSetting.title = this.metricTitle;
        this.metricParameters.startDays = range.start;
        this.metricParameters.endDays = range.end;
        this.widgetSetting.settings = JSON.stringify(this.metricParameters);

        await widgetModule.updateWidgetSetting(this.widgetSetting);
        await this.loadMetricData();
        notifications.success('Saved', 'Metric settings updated.')
    }

    getToggleClass(side: string, value: boolean){
        return `toggle ${side}${(value && side == 'left') || (!value && side == 'right') ? ' selected': ''}`;
    }

    isDrillDownAvailable() : boolean {
        return true;
    }

    showDrillDown() {
        this.isDrillDownModalVisible = true;
    }
}
